import type { DefinitionDetail, GeneratedDefinition } from "@keydials/api-client";

// ============================================================
// The one place a fetched DefinitionDetail becomes the editor's working model.
//
// This existed twice as an inline object literal (initial load, and the reload after a spreadsheet
// re-import) and the two had already drifted: BOTH silently omitted `itemSegmentRules` and
// `groupSegmentRules`, and the re-import copy had also lost `customPalettes`. An omission here is
// invisible rather than loud — the editor renders "no rules yet" for rules that are sitting safely on
// the server, and the tab looks empty instead of broken.
//
// Worse, it is a data-loss path. A field the model never loaded is absent from the PUT, which the API
// reads as "not specified, carry across" — so an untouched save is safe. But the moment the author
// adds one rule, the editor sends an array containing only that rule, and a supplied array replaces the
// whole set. The rules the editor never showed are then gone.
//
// Hence a single function with a single field list: adding a field to the editor now means adding it
// once, and {@link editModel.test} pins the fields whose loss would be silent.
// ============================================================

/** The editor edits a definition in the same shape the API accepts back. */
export type EditModel = GeneratedDefinition;

/** The editor's working copy of a fetched definition. Every authored field must be carried here. */
export function toEditModel(d: DefinitionDetail): EditModel {
  return {
    name: d.name,
    description: d.description ?? undefined,
    category: d.category ?? undefined,
    industry: d.industry ?? undefined,
    statusSet: d.statusSet,
    sections: d.sections,
    centralImage: d.centralImage,
    introVideo: d.introVideo,
    kind: d.kind,
    dimensions: d.dimensions,
    showCentralProgress: d.showCentralProgress,
    customisation: d.customisation,
    isGroup: d.isGroup,
    rollupStrategy: d.rollupStrategy,
    statusRollupDimensionKey: d.statusRollupDimensionKey,
    sectionStatusRollupDimensionKey: d.sectionStatusRollupDimensionKey,
    statusRollupStrategy: d.statusRollupStrategy,
    groupSectionStatusSource: d.groupSectionStatusSource,
    groupSectionStatusStrategy: d.groupSectionStatusStrategy,
    groupSegmentStatusSource: d.groupSegmentStatusSource,
    groupSegmentStatusStrategy: d.groupSegmentStatusStrategy,
    groupChildren: d.groupChildren,
    nodeAttributes: d.nodeAttributes,
    classificationBands: d.classificationBands,
    uploadStatusAutomation: d.uploadStatusAutomation,
    groupLinks: d.groupLinks,
    groupFaceMode: d.groupFaceMode,
    groupFaceSections: d.groupFaceSections,
    groupSegmentRules: d.groupSegmentRules,
    groupStatusRules: d.groupStatusRules,
    itemSegmentRules: d.itemSegmentRules,
    customPalettes: d.customPalettes,
    resetSchedules: d.resetSchedules,
    reviewReminderLeadDays: d.reviewReminderLeadDays,
    // KEY-534. Loaded so an unrelated save can't clear what the details form authored: the PUT's
    // "not specified" carry-across only holds while the field is absent — the moment the editor
    // sends a guidance value at all, it sends the one it was shown.
    guidance: d.guidance ?? undefined,
    guidanceVideo: d.guidanceVideo,
    // KEY-565: and what goes with it. Sharper than the prose: both are collections, and a supplied collection
    // replaces the stored one — so an editor that never loaded them would wipe them the first time it sent one.
    guidanceDocuments: d.guidanceDocuments,
    guidanceLinks: d.guidanceLinks,
    // KEY-814: the welcome page. Carried for the same reason: a save that sends it at all sends the one it was shown.
    welcome: d.welcome ?? undefined,
  };
}

/** Fields a proposal carries only between an AI draft and its first save. A saved definition never has them. */
type TransientProposalField = "groupChildProposals" | "groupLinkProposals";

/**
 * Every field of the payload, marked as carried by {@link toEditModel} (KEY-560). The `satisfies` makes a field added to
 * `GeneratedDefinition` fail typecheck here until it is listed, and `editModel.test` checks that toEditModel returns exactly these
 * keys, so the field has to be carried too. The builder in the user app saves the whole model on every change, so a field left
 * out here is not an empty tab but data deleted on the next save.
 */
export const CARRIED_FIELDS = {
  name: true,
  description: true,
  category: true,
  industry: true,
  statusSet: true,
  sections: true,
  centralImage: true,
  introVideo: true,
  kind: true,
  dimensions: true,
  showCentralProgress: true,
  customisation: true,
  isGroup: true,
  rollupStrategy: true,
  statusRollupDimensionKey: true,
  sectionStatusRollupDimensionKey: true,
  statusRollupStrategy: true,
  groupSectionStatusSource: true,
  groupSectionStatusStrategy: true,
  groupSegmentStatusSource: true,
  groupSegmentStatusStrategy: true,
  groupChildren: true,
  nodeAttributes: true,
  classificationBands: true,
  uploadStatusAutomation: true,
  groupLinks: true,
  groupFaceMode: true,
  groupFaceSections: true,
  groupSegmentRules: true,
  groupStatusRules: true,
  itemSegmentRules: true,
  customPalettes: true,
  resetSchedules: true,
  reviewReminderLeadDays: true,
  guidance: true,
  guidanceVideo: true,
  guidanceDocuments: true,
  guidanceLinks: true,
  welcome: true,
} as const satisfies Record<Exclude<keyof GeneratedDefinition, TransientProposalField>, true>;
